import {
  Badge,
  KorintSuperLink,
  PageHeader,
  PolicyHeaderActions,
  PolicyQuickActions,
  PolicyStatus,
  ToggleSuspensionButton,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useVisiblePolicyQuickActions,
} from '@folio/components';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { formatDate, formatDateWithTime, formatZonedDateTime } from '@folio/lib';
import { customerDisplayName } from '@folio/utils/customerDisplayName';
import { openStopBranch } from '@folio/utils/stop-branch';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import {
  assetControllerGetAssetQueryOptions,
  customerControllerGetCustomerQueryOptions,
} from 'korint-sdk';
import { type PolicyQuickAction, useKorintProductConfig } from 'korint-sdk/react';
import { CalendarCheck2, MoveRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TerminationButton } from './TerminationButton';
import { Group } from '@folio/components/ui/flex';

export function PolicyDashboardHeader({
  stopFunnelBasePath,
  quickActions: quickActionsFromProps,
}: {
  stopFunnelBasePath?: string;
  quickActions?: ReadonlyArray<PolicyQuickAction>;
} = {}) {
  const { policy, policyOnMain, workingBranch, branchIdForPolicy, branches } =
    usePolicyOnDashboard();
  // BASE, not the viewed branch: a STOP branch echo restarts its own lifecycle at POLICY_CREATED.
  const statusPolicy = workingBranch?.branchType === 'STOP' ? policyOnMain : policy;
  const { can } = useUserPermissions({
    productId: policy.productId,
    actions: ['STOP_POLICY', 'SUSPEND_POLICY', 'ACTIVATE_POLICY'],
  });
  const customers = useSuspenseQueries({
    queries: policy?.customers.map(customerId => ({
      ...customerControllerGetCustomerQueryOptions(customerId, {
        branchId: branchIdForPolicy,
      }),
    })),
  });
  const { t } = useTranslation();
  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policy.productId];
  const hasSeveralProducts = Object.keys(productsConfig).length > 1;
  const customerDefaultRole = productConfig?.customerDefaultRole;
  const customerInformations = usePolicyProductConfig('customerInformations');
  const quickActions = useVisiblePolicyQuickActions(quickActionsFromProps);

  const dashboardAssetFieldKey = productsConfig[policy.productId]?.dashboardAssetFieldKey;

  const firstAssetId = policy.assets?.[0];
  const { data: firstAsset } = useQuery({
    ...assetControllerGetAssetQueryOptions(firstAssetId!, {
      branchId: branchIdForPolicy,
    }),
    enabled: !!firstAssetId && !!dashboardAssetFieldKey,
  });
  const assetFieldValue = dashboardAssetFieldKey
    ? firstAsset?.customFields?.find(f => f.key === dashboardAssetFieldKey)?.value
    : undefined;

  const customerFieldsConfig = customerInformations?.fieldsConfig;
  const customerName = customerDisplayName(
    customers.map(customer => customer.data),
    customerFieldsConfig,
    customerDefaultRole,
  );
  const titleParts = [customerName, policy.externalId].filter(Boolean).join(' ');
  const title = assetFieldValue ? `${titleParts} — ${assetFieldValue}` : titleParts;

  const hasSigned = !branchIdForPolicy;

  // An open STOP branch holds the policy's only stop slot; the banner below owns resuming it.
  const displayTerminationButton =
    can.STOP_POLICY &&
    hasSigned &&
    policy.status !== 'POLICY_STOPPED' &&
    !openStopBranch(branches?.data);

  const displaySuspendButton =
    hasSigned &&
    ((can.SUSPEND_POLICY && policy.status === 'POLICY_STARTED') ||
      (can.ACTIVATE_POLICY && policy.status === 'POLICY_SUSPENDED'));

  return (
    <PageHeader
      className="mb-0"
      title={title}
      subtitle={
        <Group align="stretch" gap={2}>
          <KorintSuperLink subpath={`/policies/${policy.id}`} />
          {hasSeveralProducts && (
            <Badge variant="outline">{t(`products.${policy.productId}.label`)}</Badge>
          )}
          <PolicyStatus policy={statusPolicy} hasSigned={hasSigned} workingBranch={workingBranch} />
          {policy.insurer && <Badge variant="outline">{t(`insurer.${policy.insurer}`)}</Badge>}
          {policy.startedAt && (
            <Tooltip>
              <TooltipTrigger className="flex flex-row items-center">
                <CalendarCheck2 className="ml-2 h-4 w-4 inline" />
                <div className="ml-1">{formatDate(new Date(policy.startedAt))}</div>
              </TooltipTrigger>
              <TooltipContent>
                {t('policyDashboardHeader.effectiveDateTooltip', {
                  date: formatDateWithTime(new Date(policy.startedAt)),
                })}
              </TooltipContent>
            </Tooltip>
          )}
          {policy.stoppedAt && (
            <Tooltip>
              <TooltipTrigger className="flex flex-row items-center">
                <MoveRightIcon className="h-4 w-4 inline" />
                <div className="ml-1">{formatDate(new Date(policy.stoppedAt))}</div>
              </TooltipTrigger>
              <TooltipContent>
                {t('policyDashboardHeader.expiryDateTooltip', {
                  date: formatZonedDateTime(new Date(policy.stoppedAt), policy.timeZone),
                })}
              </TooltipContent>
            </Tooltip>
          )}
        </Group>
      }
      right={
        <PolicyHeaderActions
          gestures={
            <>
              {quickActions.length > 0 && <PolicyQuickActions actions={quickActions} />}
              {displaySuspendButton && <ToggleSuspensionButton />}
              {displayTerminationButton && (
                <TerminationButton funnelBasePath={stopFunnelBasePath} />
              )}
            </>
          }
        />
      }
    />
  );
}
